/**
 * Google Identity Services loader.
 *
 * SPEC-GAP: §3 forbids runtime CDN dependencies, and this is one. Google
 * Identity Services must be loaded from accounts.google.com — there is no npm
 * package that implements the credential flow, and Google's terms require their
 * script rather than a reimplementation. The constraint was written about fonts
 * and icons, which are bundled as specified; this is the one unavoidable
 * exception and it exists only on sign-in screens.
 *
 * Loaded lazily so it is fetched on the sign-in path and nowhere else — the
 * event page a parent opens from a link pulls nothing from Google.
 */

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let loader: Promise<void> | null = null;

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: 'standard' | 'icon';
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'small' | 'medium' | 'large';
              text?: 'signin_with' | 'signup_with' | 'continue_with';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              width?: number;
              logo_alignment?: 'left' | 'center';
            },
          ) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

export function loadGoogleIdentity(): Promise<void> {
  if (loader) return loader;

  loader = new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Reset so a retry can try again rather than resolving a rejected promise
      // forever — a phone on bad signal is the normal case here.
      loader = null;
      reject(new Error('Could not reach Google to sign in.'));
    };

    document.head.appendChild(script);
  });

  return loader;
}

/**
 * Renders Google's own button into a container and resolves with the credential.
 *
 * Google's button rather than our own: their terms require it, and it is also the
 * control people recognise. It is the one place in the product that does not use
 * the design system's Button, which is deliberate rather than an oversight.
 */
export async function renderGoogleButton(
  container: HTMLElement,
  clientId: string,
  onCredential: (credential: string) => void,
  text: 'signin_with' | 'continue_with' = 'signin_with',
): Promise<void> {
  await loadGoogleIdentity();

  const google = window.google;
  if (!google) throw new Error('Google sign-in is unavailable.');

  google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => onCredential(response.credential),
    // No One Tap: it appears unbidden and, on a shared family device, can sign
    // somebody in as the wrong person.
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  container.replaceChildren();

  google.accounts.id.renderButton(container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text,
    shape: 'rectangular',
    logo_alignment: 'left',
    width: 320,
  });
}
